import { z } from 'zod'

import { DateStringSchema, type ZodInfer } from '@mntn-dev/utility-types'

import {
  BillingServiceIdSchema,
  BillingServiceMethodIdSchema,
} from '../billing-service.models.ts'
import {
  PackageIdSchema,
  ProjectServiceIdSchema,
  ServiceIdSchema,
} from '../unique-id.models.ts'
import { ChargeProductTypeSchema } from './charge-product-type.ts'
import { ChargeStatusSchema } from './charge-status.ts'
import { InvoicePaymentTermValueSchema } from './invoice-payment-term-values.ts'
import { ProductIdSchema } from './product-type-id.ts'
import { UnitSchema } from './unit.ts'

export const ChargeLineItemSchema = z.discriminatedUnion('billingMethod', [
  // AMS billing method
  z.object({
    description: z.string().min(1),
    createdTime: DateStringSchema,
    updatedTime: DateStringSchema.optional(),
    billingMethod: z.literal('ams'),
    unit: UnitSchema,
    amount: z.number(),
    productType: ChargeProductTypeSchema,
    productId: ProductIdSchema,
    projectServiceId: ProjectServiceIdSchema.optional(),
    status: ChargeStatusSchema,
    originalAmount: z.number(),
  }),

  // Invoice billing method
  z.object({
    description: z.string().min(1),
    createdTime: DateStringSchema,
    updatedTime: DateStringSchema.optional(),
    billingMethod: z.literal('invoice'),
    invoicePaymentTerms: InvoicePaymentTermValueSchema,
    unit: UnitSchema,
    amount: z.number(),
    productType: ChargeProductTypeSchema,
    productId: ProductIdSchema,
    projectServiceId: ProjectServiceIdSchema.optional(),
    status: ChargeStatusSchema,
    originalAmount: z.number(),
  }),

  // Stripe billing method
  z.object({
    description: z.string().min(1),
    createdTime: DateStringSchema,
    updatedTime: DateStringSchema.optional(),
    billingMethod: z.literal('stripe'),
    stripeId: BillingServiceIdSchema.optional(),
    stripePaymentMethodId: BillingServiceMethodIdSchema.optional(),
    unit: UnitSchema,
    amount: z.number(),
    productType: ChargeProductTypeSchema,
    productId: z.union([PackageIdSchema, ServiceIdSchema]),
    projectServiceId: ProjectServiceIdSchema.optional(),
    status: ChargeStatusSchema,
    originalAmount: z.number(),
  }),

  // Credit program billing method
  z.object({
    description: z.string().min(1),
    createdTime: DateStringSchema,
    updatedTime: DateStringSchema.optional(),
    billingMethod: z.literal('mntn_credits'),
    unit: z.literal('mntn_credits'),
    amount: z.number(),
    originalAmount: z.number(),
    productType: ChargeProductTypeSchema,
    productId: z.union([PackageIdSchema, ServiceIdSchema]),
    projectServiceId: ProjectServiceIdSchema.optional(),
    status: ChargeStatusSchema,
  }),

  // Meta billing method
  z.object({
    description: z.string().min(1),
    createdTime: DateStringSchema,
    updatedTime: DateStringSchema.optional(),
    billingMethod: z.literal('meta'),
    unit: z.literal('meta'),
    amount: z.number(),
    productType: ChargeProductTypeSchema,
    productId: z.union([PackageIdSchema, ServiceIdSchema]),
    projectServiceId: ProjectServiceIdSchema.optional(),
    status: ChargeStatusSchema,
    originalAmount: z.number(),
  }),

  // TikTok billing method
  z.object({
    description: z.string().min(1),
    createdTime: DateStringSchema,
    updatedTime: DateStringSchema.optional(),
    billingMethod: z.literal('tiktok'),
    unit: z.literal('tiktok'),
    amount: z.number(),
    productType: ChargeProductTypeSchema,
    productId: z.union([PackageIdSchema, ServiceIdSchema]),
    projectServiceId: ProjectServiceIdSchema.optional(),
    status: ChargeStatusSchema,
    originalAmount: z.number(),
  }),

  // LinkedIn billing method
  z.object({
    description: z.string().min(1),
    createdTime: DateStringSchema,
    updatedTime: DateStringSchema.optional(),
    billingMethod: z.literal('linkedin'),
    unit: z.literal('linkedin'),
    amount: z.number(),
    productType: ChargeProductTypeSchema,
    productId: z.union([PackageIdSchema, ServiceIdSchema]),
    projectServiceId: ProjectServiceIdSchema.optional(),
    status: ChargeStatusSchema,
    originalAmount: z.number(),
  }),
])

export type ChargeLineItem = ZodInfer<typeof ChargeLineItemSchema>
